import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface CelestialAudioHandle {
  activate: () => void;
}

function browserSupportsAudio() {
  return typeof window !== "undefined"
    && typeof window.HTMLMediaElement !== "undefined"
    && typeof window.HTMLMediaElement.prototype.play === "function";
}

export const CelestialAudio = forwardRef<CelestialAudioHandle, { enabled: boolean }>(function CelestialAudio(
  { enabled },
  ref,
) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const player = useRef<HTMLAudioElement>(null);
  const generation = useRef(0);
  const isPlaying = useRef(false);
  const requestPending = useRef(false);
  const supported = browserSupportsAudio();

  const stop = useCallback(() => {
    generation.current += 1;
    requestPending.current = false;
    isPlaying.current = false;
    setPlaying(false);
    setFailed(false);
    const audio = player.current;
    if (!audio) return;
    try {
      audio.pause();
      audio.currentTime = 0;
    } catch {
      // The browser may not have loaded media metadata yet.
    }
  }, []);

  const start = useCallback(() => {
    const audio = player.current;
    if (!audio || !supported || isPlaying.current || requestPending.current) return;

    if (!audio.getAttribute("src")) audio.setAttribute("src", "/celestial-song.mp3");
    audio.volume = 0.12;
    requestPending.current = true;
    const request = ++generation.current;
    setFailed(false);
    isPlaying.current = true;
    setPlaying(true);

    try {
      void audio.play().then(() => {
        if (generation.current !== request) {
          if (!requestPending.current && !isPlaying.current) audio.pause();
          return;
        }
        requestPending.current = false;
      }).catch(() => {
        if (generation.current !== request) return;
        requestPending.current = false;
        isPlaying.current = false;
        setPlaying(false);
        setFailed(true);
      });
    } catch {
      requestPending.current = false;
      isPlaying.current = false;
      setPlaying(false);
      setFailed(true);
    }
  }, [supported]);

  useImperativeHandle(ref, () => ({ activate: start }), [start]);

  useEffect(() => {
    if (enabled) start();
  }, [enabled, start]);

  useLayoutEffect(() => () => {
    if (enabled) stop();
  }, [enabled, stop]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") stop();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [stop]);

  return (
    <>
      <audio
        ref={player}
        className="celestial-audio-control__player"
        aria-hidden="true"
        tabIndex={-1}
        preload="none"
        loop
      />
      {enabled ? (
        <div className="celestial-audio-control">
          <button
            type="button"
            className="celestial-audio-control__button"
            aria-label={t(playing ? "holy.audioStop" : "holy.audioStart")}
            aria-pressed={playing}
            disabled={!supported}
            onClick={() => playing ? stop() : start()}
          >
            {t(playing ? "holy.audioStop" : "holy.audioStart")}
          </button>
          {!supported ? <span className="celestial-audio-control__message" role="status">{t("holy.audioUnavailable")}</span> : null}
          {failed ? <span className="celestial-audio-control__message" role="status">{t("holy.audioBlocked")}</span> : null}
        </div>
      ) : null}
    </>
  );
});
